import {ReactElement, useEffect, useState} from "react"
import {useParams} from "react-router-dom"
import * as dayjs from "dayjs"
import {useTranslate} from "@refinedev/core"
import {Edit} from "@refinedev/antd"
import {Button, Col, Form, FormListFieldData, Input, InputNumber, Row, Select, TimePicker} from "antd"
import TextArea from "antd/es/input/TextArea"
import {EyeOutlined} from "@ant-design/icons"
import IClient from "../../interfaces/IClient"
import InstanceCard from "../../components/cards/InstanceCard"
import {createActivity} from "../../services/ServiceActivity"
import {getClients} from "../../services/ServiceClient"
import {getAuthentications} from "../../services/ServiceAuthentication"
import {getTypes} from "../../services/ServiceType"
import ClientCard from "../../components/cards/ClientCard"
import {Custom} from "../../utils/refineUtils"

export default function ActivityEdit(): ReactElement | null {
    const {id} = useParams()
    const translate = useTranslate()
    const [clientsSelected, setClientsSelected] = useState<string[]>([])
    const {activity, formProps, saveButtonProps, loadingForm} = createActivity(id)
    const {clients, isClientsLoading} = getClients()
    const {authentications, isAuthenticationsLoading} = getAuthentications()
    const {types, isTypesLoading} = getTypes()
    const deleteButtonProps = {
        resource: 'activity',
        confirmTitle: translate('buttonsProps.delete.confirmTitle'),
        confirmOkText: translate('buttonsProps.delete.confirmOkText'),
        confirmCancelText: translate('buttonsProps.delete.confirmCancelText'),
        recordItemId: id
    }

    const toggleClient = (client: IClient, index: number): void => {
        setClientsSelected(clientsSelected.includes(client.id) ? clientsSelected.filter(cl => cl !== client.id) : clientsSelected.concat(client.id));
        // We add client in the list, so we need to put his id in the "clients" key
        if (!clientsSelected.includes(client.id)) {
            formProps?.form?.setFieldValue(["clients", index], {
                ...formProps?.form?.getFieldValue("clients")[index],
                id: client.id
            })
        }
    }

    const handleOnFinish = (values: any) => {
        const payload = {...activity, ...values}
        payload.clients = values.clients
        payload.clients = payload?.clients?.filter((client: any) => client && clientsSelected.includes(client.id))
        formProps.onFinish && formProps.onFinish(payload);
    };

    //@ts-ignore
    const headerButtons = () => (
        <>
            <a href={activity?.url} target="_blank">
                <Button type="default" icon={<EyeOutlined/>}>{translate("buttons.preview")}</Button>
            </a>
        </>
    )

    const initialValues = () => {
        let values = {...activity}
        values?.instances?.forEach(activity => {
            activity.start = dayjs(activity.start)
            activity.end = dayjs(activity.end)
        })
        return values;
    }

    useEffect(() => {
        if (!activity) return;
        setClientsSelected(activity?.clients?.map(client => client.id))
    }, [activity])

    return <Edit title={activity?.title} headerButtons={headerButtons}
                 saveButtonProps={{...saveButtonProps, children: translate('buttons.save')}}
                 deleteButtonProps={deleteButtonProps}
                 isLoading={loadingForm || isClientsLoading || isAuthenticationsLoading || isTypesLoading}>
        <Form labelWrap layout="horizontal" labelAlign="left" {...formProps} initialValues={initialValues()}
              onFinish={handleOnFinish}>
            <Row gutter={16}>
                <Col span={12}>
                    <Custom.Item name="title" label="activity.title" icon="notification" required>
                        <Input/>
                    </Custom.Item>
                    <Custom.Item name="author" label="activity.author" icon="user" required>
                        <Input/>
                    </Custom.Item>
                    <Custom.Item name="description" label="activity.description" icon="align-left">
                        <TextArea rows={4}/>
                    </Custom.Item>
                    <Custom.Item name="duration" label="activity.duration" icon="clock-circle">
                        <InputNumber min={0} style={{width: '100%'}}/>
                        {/*<TimePicker style={{width: '100%'}} showNow={false} format="HH:mm"/>*/}
                    </Custom.Item>
                    <Custom.Item name="url" label="activity.url" icon="link" required>
                        <Input/>
                    </Custom.Item>
                </Col>
                <Col span={12}>
                    <Custom.Item name="authentications" label="activity.authentications" icon="login" required>
                        <Select allowClear mode="multiple"
                                options={authentications?.map(auth => ({label: auth.name, value: auth.id}))}
                                tagRender={(props) => {
                                    const auth = authentications.find(a => a.id === props.value)
                                    const type = types.find(t => t.id === auth?.type)
                                    return <Custom.Tag {...props} auth={auth} type={type}/>
                                }}/>
                    </Custom.Item>
                    <Custom.List name="clients" label="activity.clients" icon="crown" required>
                        {activity?.clients?.map((client, index: number) => (
                            <ClientCard key={client.id} editable checked
                                        onChange={(c) => toggleClient(c, index)} form={formProps?.form}
                                        client={clients?.find(cl => cl.id === client.id)!} index={index}/>
                        ))}
                        {clients?.filter(client => !activity?.clients?.some(cl => cl.id === client.id))?.map((client: IClient, index: number) => {
                            const realIndex: number = index + clients?.filter(client => activity?.clients?.some(cl => cl.id === client.id)).length
                            return (
                                <ClientCard key={client.id} editable onChange={(c) => toggleClient(c, realIndex)}
                                            form={formProps?.form} client={client} index={realIndex}/>
                            )
                        })}
                    </Custom.List>
                </Col>
            </Row>
            <Custom.List name="instances" label="activity.instances" icon="calendar" editable>
                {(formList: FormListFieldData[], remove: (index: number | number[]) => void) => <>
                    {formList.map(({key, name: index}) => (
                        <InstanceCard key={key} editable index={index} form={formProps?.form} onDelete={remove}
                                      activity={activity}/>))}
                    {formList.length === 0 && <Row>{translate('activity.noInstance')}</Row>}
                </>}
            </Custom.List>
        </Form>
    </Edit>
}
